import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { ArticleEntryData } from "@/types/articleEntryData.types";

/**
 * Deal 의 Key Comment List(MA/VF 는 Deals, LA 는 Deal LA&SC 섹션) 에서
 * quote·spokesperson 이 앞선 행과 "완전히 동일한" 중복 행을 제거한다.
 * (첫 행은 유지, 뒤따르는 중복 행만 제거)
 *
 * - quote·spokesperson 둘 다 빈 행은 중복 제거 대상에서 제외한다.
 *   (빈 행은 여러 개 존재해도 그대로 둔다.)
 * - Key Comment List 그룹테이블이 아니면 원본 entryData 를 그대로 반환한다.
 */
export const dedupeKeyCommentListInEntryData = (
  entryData: ArticleEntryData,
  articleSection: string,
  groupTableKey: string,
): ArticleEntryData => {
  if (groupTableKey !== "Key Comment List") return entryData;

  const keyCommentSection =
    articleSection === ENTRY_DATA_SECTION_KEYS.dealsLaSc
      ? ENTRY_DATA_SECTION_KEYS.dealsLaSc
      : ENTRY_DATA_SECTION_KEYS.deals;

  const keyCommentList = entryData[keyCommentSection]?.["Key Comment List"];

  if (!Array.isArray(keyCommentList) || keyCommentList.length <= 1)
    return entryData;

  const seenRowKeys = new Set<string>();

  const dedupedList = keyCommentList.filter((item) => {
    const quote = item?.["Comment Quote"]?.value?.[0]?.text ?? "";
    const spokesperson = item?.["Comment Spokesperson"]?.value?.[0]?.text ?? "";

    // 빈값(둘 다 공백) 행은 중복 제거하지 않고 유지한다.
    if (quote === "" && spokesperson === "") return true;

    // (quote, spokesperson) 쌍을 구분자 충돌 없이 식별한다.
    const rowKey = JSON.stringify([quote, spokesperson]);

    if (seenRowKeys.has(rowKey)) return false;

    seenRowKeys.add(rowKey);
    return true;
  });

  if (dedupedList.length === keyCommentList.length) return entryData;

  return {
    ...entryData,
    [keyCommentSection]: {
      ...entryData[keyCommentSection],
      "Key Comment List": dedupedList,
    },
  } as ArticleEntryData;
};
